// Enhanced FileItem Component with improved accessibility
import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { FiFilm, FiImage, FiCheckCircle, FiX, FiRefreshCw } from 'react-icons/fi';
import { ANIMATIONS } from '../utils/constants';
import { formatFileSize } from '../utils/helpers';

/**
 * Accessible component to display a single file with its upload status
 * - Enhanced screen reader support
 * - Improved keyboard navigation
 * - Better status announcements
 *
 * @param {Object} props - Component props
 * @param {Object} props.file - File object with metadata
 * @param {number} props.progress - Upload progress (0-100, or -1 for error)
 * @param {boolean} props.isCurrentlyUploading - Whether this file is currently being uploaded
 * @param {boolean} props.isUploading - Whether any file is uploading
 * @param {boolean} props.isComplete - Whether upload is complete
 * @param {Function} props.onRemove - Function to remove this file
 * @param {Function} props.onRetry - Function to retry upload for this file
 * @param {Object} ref - Forwarded ref for scrolling
 * @returns {JSX.Element} Accessible file item component
 */
const FileItem = forwardRef(
  ({ file, progress, isCurrentlyUploading, isUploading, isComplete, onRemove, onRetry }, ref) => {
    const { name, size, isVideo } = file;
    const isSuccess = progress === 100;
    const isError = progress === -1;
    const isUploaded = progress !== undefined;

    // Determine status for both visual and screen reader users
    let statusClass = '';
    let statusText = '';

    if (isSuccess) {
      statusClass = 'bg-success bg-opacity-10 border-success file-success';
      statusText = 'Upload complete';
    } else if (isError) {
      statusClass = 'bg-danger bg-opacity-10 border-danger file-error';
      statusText = 'Upload failed';
    } else if (isCurrentlyUploading) {
      statusClass = 'bg-light border-primary file-uploading';
      statusText = `Uploading ${progress}%`;
    } else if (progress === undefined) {
      statusText = 'Pending upload';
    }

    return (
      <motion.div
        ref={ref}
        variants={ANIMATIONS.itemVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className={`file-item ${isCurrentlyUploading ? 'currently-uploading' : ''}`}
        role="listitem">
        <div
          className={`file-card p-3 border rounded d-flex align-items-center ${statusClass}`}
          role="group"
          aria-labelledby={`file-name-${file.id}`}
          aria-describedby={`file-status-${file.id} file-details-${file.id}`}>
          {/* File type icon */}
          <div className="file-icon-container me-3 fs-6" aria-hidden="true">
            {isVideo ? (
              <FiFilm size={22} className="text-danger icon-transparent size-medium-large" />
            ) : (
              <FiImage size={22} className="text-primary icon-transparent size-medium-large" />
            )}
          </div>

          {/* File details */}
          <div className="flex-grow-1 overflow-hidden">
            <p
              id={`file-name-${file.id}`}
              className="small fw-medium mb-0 text-truncate font-size-medium">
              {name}{' '}
              <span className="visually-hidden">{isVideo ? '(Video file)' : '(Image file)'}</span>
            </p>

            <p id={`file-details-${file.id}`} className="small font-size-medium text-muted mb-1">
              {formatFileSize(size)}
            </p>

            {/* Status for screen readers */}
            <div
              id={`file-status-${file.id}`}
              className={isSuccess || isError ? '' : 'visually-hidden font-size-small'}>
              {statusText}
            </div>

            {/* Progress bar */}
            {isUploaded && (
              <div
                className="progress mt-1"
                style={{ height: '6px', borderRadius: '3px' }}
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={progress >= 0 ? progress : 0}
                aria-label={`Upload progress for ${name}`}>
                <div
                  className={`progress-bar ${isError ? 'bg-danger' : 'bg-primary'}`}
                  style={{
                    width: `${progress >= 0 ? progress : 0}%`,
                    transition: 'width 0.3s ease',
                  }}></div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="file-actions d-flex align-items-center">
            {isSuccess ? (
              <div className="file-success-indicator font-size-large" aria-hidden="true">
                <FiCheckCircle
                  size={18}
                  className="text-success ms-2 icon-transparent size-medium-plus"
                />
              </div>
            ) : isError ? (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onRetry();
                }}
                disabled={isUploading}
                className="btn btn-sm btn-outline-danger p-1 d-flex align-items-center justify-content-center retry-button font-size-large"
                title="Retry upload"
                aria-label={`Retry upload for ${name}`}>
                <FiRefreshCw size={14} aria-hidden="true" className="icon-transparent size-small" />
              </button>
            ) : !isUploading && !isComplete ? (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="btn btn-sm btn-link text-danger p-1 remove-button font-size-large"
                title="Remove file"
                aria-label={`Remove ${name} from upload list`}>
                <FiX size={18} aria-hidden="true" className="icon-transparent size-medium-plus" />
              </button>
            ) : null}
          </div>
        </div>
      </motion.div>
    );
  }
);

FileItem.displayName = 'FileItem';

export default FileItem;
